// ============================================================================
// MÓDULO 10: AGENDA MANAGER (RDA CORE 2.6 - GOOGLE MOCK DATA INJECTION)
// ARQUIVO: src/10_Agenda_Manager.jsx
// OBJETIVO: Calendário Visual + Ponte Timesheet + Simulação Realista de Sync.
// MELHORIAS V2.6 + FIX V2.8 (Feedback Visual de Envio): 
// - Injeção de dados "Fake" ao conectar Google.
// - Botão "Lançar Horas" agora atualiza para "Enviado" (Persistência).
// ============================================================================

import React, { useState, useEffect } from 'react';
import { loadAgendaEvents, addAgendaEvent, deleteAgendaEvent, markAgendaTimesheetSent } from '../../services/AgendaManagerRepository';
import { FIREBASE_CONFIG } from '../../config/Config';
import { SaveCancelBar } from '../../components/ui/SaveCancelBar';

// --- CONSTANTES ---
const WEEKDAYS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

const EVENT_TYPES = {
    'DEADLINE': { label: 'Prazo Fatal', color: 'bg-red-100 text-red-800 border-red-200', icon: 'fa-hourglass-end' },
    'HEARING': { label: 'Audiência', color: 'bg-purple-100 text-purple-800 border-purple-200', icon: 'fa-gavel' },
    'MEETING': { label: 'Reunião', color: 'bg-blue-100 text-blue-800 border-blue-200', icon: 'fa-users' },
    'TASK': { label: 'Tarefa', color: 'bg-gray-100 text-gray-800 border-gray-200', icon: 'fa-check-square' },
    // Tipo especial para eventos externos
    'GOOGLE': { label: 'Google Agenda', color: 'bg-white text-gray-600 border-gray-300', icon: 'fab fa-google' } 
};

// --- MODAL DE CONFIGURAÇÃO GOOGLE CALENDAR ---
const GoogleSyncModal = ({ onClose, onConnect, isConnected, lastSync }) => {
    const [loading, setLoading] = useState(false);

    const handleAuthClick = () => {
        setLoading(true);
        // SIMULAÇÃO DO FLUXO OAUTH2
        setTimeout(() => {
            setLoading(false);
            onConnect(true);
        }, 1500);
    };

    const handleDisconnect = () => {
        if(confirm("Desconectar sua conta Google? Os eventos externos serão removidos da visualização.")) {
            onConnect(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-gray-900/60 backdrop-blur-sm">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm overflow-hidden border-t-4 border-blue-500 animate-fade-in-up">
                <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50">
                    <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                        <i className="fab fa-google text-blue-600"></i> Google Calendar
                    </h3>
                    <button onClick={onClose}><i className="fas fa-times text-gray-400 hover:text-red-500"></i></button>
                </div>
                
                <div className="p-6 text-center">
                    {!isConnected ? (
                        <>
                            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <i className="fas fa-plug text-2xl text-gray-400"></i>
                            </div>
                            <h4 className="font-bold text-gray-700 mb-2">Conectar Conta</h4>
                            <p className="text-xs text-gray-500 mb-6 leading-relaxed">
                                Autorize o RDA a ler e gravar eventos na sua agenda. <br/>
                                <span className="text-orange-600 font-bold bg-orange-50 px-1 rounded mt-1 inline-block">MODO SIMULAÇÃO ATIVO</span>
                            </p>
                            <button 
                                onClick={handleAuthClick} 
                                disabled={loading}
                                className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs rounded shadow-lg transition-all flex items-center justify-center gap-2"
                            >
                                {loading ? <i className="fas fa-circle-notch fa-spin"></i> : <i className="fab fa-google"></i>}
                                {loading ? 'IMPORTANDO DADOS...' : 'AUTORIZAR COM GOOGLE'}
                            </button>
                        </>
                    ) : (
                        <>
                            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4 relative">
                                <i className="fas fa-check text-2xl text-green-600"></i>
                                <span className="absolute bottom-0 right-0 w-4 h-4 bg-green-500 border-2 border-white rounded-full"></span>
                            </div>
                            <h4 className="font-bold text-green-700 mb-1">Conta Sincronizada</h4>
                            <p className="text-xs text-gray-500 mb-6">
                                Última sincronização: <strong>{lastSync || 'Agora mesmo'}</strong>
                            </p>
                            
                            <div className="space-y-2">
                                <button 
                                    onClick={() => window.open('https://calendar.google.com', '_blank')}
                                    className="w-full py-2 border border-gray-300 text-gray-600 font-bold text-xs rounded hover:bg-gray-50 transition-colors"
                                >
                                    ABRIR CALENDÁRIO <i className="fas fa-external-link-alt ml-1"></i>
                                </button>
                                <button 
                                    onClick={handleDisconnect}
                                    className="w-full py-2 text-red-500 font-bold text-xs hover:bg-red-50 rounded transition-colors"
                                >
                                    DESCONECTAR
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

// --- CALCULADORA DE PRAZOS (CPC/2015) ---
const CPCCalculator = ({ onCalculate }) => {
    const [startDate, setStartDate] = useState('');
    const [days, setDays] = useState(15);

    const calculateDeadline = () => {
        if (!startDate) return;
        let count = 0;
        let current = new Date(startDate + 'T00:00:00');
        
        while (count < days) {
            current.setDate(current.getDate() + 1);
            const day = current.getDay();
            if (day !== 0 && day !== 6) {
                count++;
            }
        }
        onCalculate(current);
    };

    return (
        <div className="bg-gray-50 p-4 rounded-xl border border-gray-300 shadow-sm mb-6 relative overflow-hidden">
            <div className="absolute top-0 right-0 p-2 opacity-10">
                <i className="fas fa-calculator text-6xl text-[#20243B]"></i>
            </div>
            <h3 className="text-xs font-bold text-[#20243B] uppercase tracking-wide mb-3 flex items-center gap-2">
                <i className="fas fa-balance-scale"></i> Calculadora de Prazos (CPC/15)
            </h3>
            <div className="flex flex-col md:flex-row gap-4 items-end relative z-10">
                <div className="flex-1 w-full">
                    <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">Data da Intimação / Publicação</label>
                    <input type="date" className="w-full p-2 border border-gray-300 rounded text-xs focus:ring-2 focus:ring-[#20243B] outline-none bg-white" value={startDate} onChange={e => setStartDate(e.target.value)} />
                </div>
                <div className="w-24">
                    <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">Dias Úteis</label>
                    <input type="number" className="w-full p-2 border border-gray-300 rounded text-xs focus:ring-2 focus:ring-[#20243B] outline-none text-center font-bold bg-white" value={days} onChange={e => setDays(parseInt(e.target.value))} />
                </div>
                <button onClick={calculateDeadline} className="w-full md:w-auto px-6 py-2 bg-[#20243B] text-white rounded text-xs font-bold hover:opacity-90 transition-colors h-[34px] flex items-center justify-center gap-2 shadow-md active:scale-95">
                    <i className="fas fa-calculator"></i> CALCULAR VENCIMENTO
                </button>
            </div>
            <p className="text-[9px] text-gray-400 mt-2 italic">* Contagem exclui Sábados e Domingos. Verifique feriados locais manualmente.</p>
        </div>
    );
};

// --- COMPONENTE: CÉLULA DO CALENDÁRIO ---
const CalendarCell = ({ day, monthDate, events, isToday, onSelect }) => {
    const isCurrentMonth = day.getMonth() === monthDate.getMonth();
    const dayOfWeek = day.getDay(); // 0 = Dom, 6 = Sáb
    const dayEvents = events.filter(e => e.date === day.toISOString().split('T')[0]);

    // LÓGICA VISUAL DE CORES
    let bgClass = "bg-white"; 
    let textClass = "text-gray-700";
    let borderClass = "border-gray-300";

    if (!isCurrentMonth) {
        bgClass = "bg-gray-200/80"; 
        textClass = "text-gray-400 font-normal"; 
    } else if (dayOfWeek === 0) {
        bgClass = "bg-red-200"; 
        textClass = "text-red-900 font-bold";
        borderClass = "border-red-300";
    } else if (dayOfWeek === 6) {
        bgClass = "bg-slate-200"; 
        textClass = "text-slate-700 font-bold";
        borderClass = "border-slate-300";
    }

    return (
        <div 
            onClick={() => onSelect(day)}
            className={`
                min-h-[110px] border-b border-r ${borderClass} p-1 relative transition-all cursor-pointer group 
                ${bgClass} hover:brightness-95 hover:shadow-inner hover:z-10
            `}
        >
            <div className="flex justify-between items-start">
                <span className={`text-xs font-bold p-1 rounded-full w-6 h-6 flex items-center justify-center ${isToday ? 'bg-brand-blue text-white shadow-md scale-110' : textClass}`}>
                    {day.getDate()}
                </span>
                {/* Indicador de Quantidade (Mobile) */}
                {dayEvents.length > 0 && <span className="lg:hidden w-1.5 h-1.5 bg-brand-blue rounded-full"></span>}
            </div>
            
            <div className="mt-1 space-y-1 overflow-y-auto max-h-[75px] custom-scrollbar hidden lg:block">
                {dayEvents.map((evt, idx) => {
                    const isGoogle = evt.source === 'GOOGLE';
                    const style = EVENT_TYPES[evt.type] || EVENT_TYPES['TASK'];
                    
                    return (
                        <div 
                            key={evt.id || idx} 
                            className={`text-[9px] px-1.5 py-1 rounded truncate font-medium border-l-2 shadow-sm ${isGoogle ? 'bg-orange-50 text-gray-600 border-orange-400' : (style.color || 'bg-white text-gray-600 border-gray-400')}`}
                            title={evt.title}
                        >
                            {isGoogle && <i className="fab fa-google text-orange-500 mr-1"></i>}
                            <span className="font-bold mr-1">{evt.time}</span>
                            {evt.title}
                        </div>
                    );
                })}
            </div>
            
            <button className="absolute bottom-1 right-1 opacity-0 group-hover:opacity-100 text-blue-600 hover:bg-blue-200 p-1.5 rounded transition-all">
                <i className="fas fa-plus text-xs"></i>
            </button>
        </div>
    );
};

// --- MAIN MANAGER ---
export const AgendaManager = ({ extCurrentDate = null, onCurrentDateChange = null, newEventTrigger = 0, syncTrigger = 0, onGoogleConnectedChange = null }) => {
    const [currentDateInternal, setCurrentDateInternal] = useState(new Date());
    const currentDate = extCurrentDate !== null ? extCurrentDate : currentDateInternal;
    const setCurrentDate = (v) => { setCurrentDateInternal(v); if (onCurrentDateChange) onCurrentDateChange(v); };
    const [localEvents, setLocalEvents] = useState([]);
    const [externalEvents, setExternalEvents] = useState([]); // Eventos do "Google" (Mock)
    
    const [showModal, setShowModal] = useState(false);
    const [showSyncModal, setShowSyncModal] = useState(false);
    const [isGoogleConnected, setIsGoogleConnected] = useState(false);
    const [lastSyncTime, setLastSyncTime] = useState(null);

    const [form, setForm] = useState({ 
        title: '', date: '', time: '09:00', type: 'DEADLINE', description: '', 
        meetLink: '' 
    });

    const appId = FIREBASE_CONFIG.projectId;

    useEffect(() => { loadEvents(); }, [currentDate]);

    const loadEvents = async () => {
        try {
            const rows = await loadAgendaEvents(appId);
            setLocalEvents(rows);
        } catch (e) { console.error("Erro agenda:", e); }
    };

    // --- LÓGICA DE SIMULAÇÃO DE DADOS EXTERNOS ---
    const generateMockGoogleEvents = () => {
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth(); // 0-indexed
        
        // Gera datas relativas ao mês atual visualizado
        const getIsoDate = (day) => new Date(year, month, day).toISOString().split('T')[0];

        return [
            { id: 'g1', title: 'Feriado Nacional', date: getIsoDate(7), time: '00:00', type: 'GOOGLE', description: 'Independência / Feriado', source: 'GOOGLE' },
            { id: 'g2', title: 'Almoço com Cliente', date: getIsoDate(12), time: '12:30', type: 'GOOGLE', description: 'Restaurante X', source: 'GOOGLE' },
            { id: 'g3', title: 'Consulta Dentista', date: getIsoDate(22), time: '16:00', type: 'GOOGLE', description: 'Dr. Silva', source: 'GOOGLE' },
            { id: 'g4', title: 'Aniversário João', date: getIsoDate(28), time: '19:00', type: 'GOOGLE', description: 'Jantar em família', source: 'GOOGLE' },
        ];
    };

    useEffect(() => {
        if (newEventTrigger > 0) {
            setForm({ title: '', date: new Date().toISOString().split('T')[0], time: '09:00', type: 'TASK', description: '', meetLink: '' });
            setShowModal(true);
        }
    }, [newEventTrigger]);

    useEffect(() => {
        if (syncTrigger > 0) setShowSyncModal(true);
    }, [syncTrigger]);

    const handleGoogleConnect = (status) => {
        setIsGoogleConnected(status);
        if (onGoogleConnectedChange) onGoogleConnectedChange(status);
        if (status) {
            // MODO SIMULAÇÃO: Injeta eventos fake para o usuário "sentir" o sync
            const mockData = generateMockGoogleEvents();
            setExternalEvents(mockData);
            
            const now = new Date().toLocaleString();
            setLastSyncTime(now);
            alert(`Conexão Simulada com Sucesso!\nForam importados ${mockData.length} eventos do Google Calendar para demonstração.`);
        } else {
            setExternalEvents([]);
            setLastSyncTime(null);
        }
    };

    // COMBINAÇÃO DOS EVENTOS (RDA + GOOGLE MOCK)
    const combinedEvents = [...localEvents, ...externalEvents];

    const handleCalculateResult = (resultDate) => {
        setForm({
            ...form,
            date: resultDate.toISOString().split('T')[0],
            title: 'Prazo Fatal (Calculado)',
            time: '23:59', 
            type: 'DEADLINE',
            description: 'Vencimento calculado automaticamente via CPC/2015 (Dias Úteis).'
        });
        setShowModal(true);
    };

    const handleSave = async () => {
        try {
            await addAgendaEvent({ ...form }, appId);
            setShowModal(false);
            loadEvents();
        } catch (e) { alert("Erro ao salvar: " + e.message); }
    };

    const handleDelete = async (id, source) => {
        if (source === 'GOOGLE') {
            alert("Não é possível deletar eventos do Google Calendar por aqui (Somente Leitura).");
            return;
        }
        if (!confirm("Excluir este evento?")) return;
        try { await deleteAgendaEvent(id, appId); loadEvents(); } catch (e) { console.error(e); }
    };

    // --- CORREÇÃO V2.8: FEEDBACK VISUAL DE ENVIO ---
    const handleSendToTimesheet = async (evt) => {
        const payload = {
            clientName: evt.title,
            description: `Atuação em: ${evt.type} - ${evt.description}`,
            autoStart: true
        };
        localStorage.setItem('rda_timesheet_buffer', JSON.stringify(payload));
        
        // Atualiza estado visual
        if (evt.source === 'RDA' && evt.id) {
             try {
                 await markAgendaTimesheetSent(evt.id, appId);
                 setLocalEvents(prev => prev.map(e => e.id === evt.id ? { ...e, timesheetSent: true } : e));
             } catch (e) { console.error("Erro status:", e); }
        } else {
             // Mock update for Google events
             setExternalEvents(prev => prev.map(e => e.id === evt.id ? { ...e, timesheetSent: true } : e));
        }

        alert("✅ Enviado para o Timesheet!\n\nAbra o módulo Timesheet e o cronômetro iniciará automaticamente.");
    };

    const generateMeetLink = () => {
        const mockLink = `https://meet.google.com/${Math.random().toString(36).substr(2, 3)}-${Math.random().toString(36).substr(2, 4)}-${Math.random().toString(36).substr(2, 3)}`;
        setForm(prev => ({ ...prev, meetLink: mockLink }));
    };

    const generateCalendarGrid = () => {
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();
        const firstDayOfMonth = new Date(year, month, 1);
        const daysInMonth = new Date(year, month + 1, 0).getDate();
        const startingDayOfWeek = firstDayOfMonth.getDay(); 
        
        const days = [];
        for (let i = 0; i < startingDayOfWeek; i++) { days.push(new Date(year, month, -startingDayOfWeek + 1 + i)); }
        for (let i = 1; i <= daysInMonth; i++) { days.push(new Date(year, month, i)); }
        const remaining = 42 - days.length;
        for (let i = 1; i <= remaining; i++) { days.push(new Date(year, month + 1, i)); }
        return days;
    };

    const calendarDays = generateCalendarGrid();

    return (
        <div>
            <CPCCalculator onCalculate={handleCalculateResult} />

            {/* Calendário Visual */}
            <div className="bg-white rounded-xl shadow-lg border border-gray-300 overflow-hidden">
                <div className="grid grid-cols-7 border-b-2 border-gray-300 bg-gray-100">
                    {WEEKDAYS.map((day, idx) => (
                        <div key={day} className={`py-3 text-center text-xs font-extrabold uppercase tracking-widest border-r border-gray-300 last:border-0 ${idx === 0 ? 'text-red-800 bg-red-100' : (idx === 6 ? 'text-slate-800 bg-slate-100' : 'text-gray-600')}`}>
                            {day}
                        </div>
                    ))}
                </div>
                <div className="grid grid-cols-7 bg-gray-300 gap-[1px] border-b border-gray-300"> 
                    {calendarDays.map((day, idx) => (
                        <CalendarCell 
                            key={idx} 
                            day={day} 
                            monthDate={currentDate} 
                            events={combinedEvents} // USA A LISTA COMBINADA
                            isToday={day.toDateString() === new Date().toDateString()} 
                            onSelect={(d) => { setForm(prev => ({...prev, date: d.toISOString().split('T')[0]})); setShowModal(true); }} 
                        />
                    ))}
                </div>
            </div>

            {/* Lista Próximos Eventos */}
            <div className="mt-8">
                <h3 className="text-sm font-bold text-gray-600 uppercase mb-4 border-b border-gray-200 pb-2">Próximos Compromissos</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {combinedEvents
                        .filter(e => new Date(e.date) >= new Date())
                        .sort((a,b) => new Date(a.date) - new Date(b.date))
                        .slice(0, 6)
                        .map((evt, idx) => (
                        <div key={evt.id || idx} className={`bg-white p-4 rounded-lg border shadow-sm flex flex-col justify-between group hover:border-brand-blue transition-colors ${evt.source === 'GOOGLE' ? 'border-orange-200 bg-orange-50/20' : 'border-gray-200'}`}>
                            <div className="flex justify-between items-start">
                                <div>
                                    <div className="flex items-center gap-2 mb-1">
                                        <span className={`text-[9px] font-bold px-2 py-0.5 rounded border ${evt.source === 'GOOGLE' ? 'bg-white text-orange-600 border-orange-200' : EVENT_TYPES[evt.type]?.color}`}>
                                            {evt.source === 'GOOGLE' && <i className="fab fa-google mr-1"></i>}
                                            {EVENT_TYPES[evt.type]?.label || 'Evento Externo'}
                                        </span>
                                        <span className="text-xs text-gray-400 font-mono">{new Date(evt.date).toLocaleDateString('pt-BR')} {evt.time}</span>
                                    </div>
                                    <h4 className="text-sm font-bold text-gray-800 line-clamp-1">{evt.title}</h4>
                                    <p className="text-xs text-gray-500 mt-1 line-clamp-1">{evt.description}</p>
                                    {evt.meetLink && <a href={evt.meetLink} target="_blank" rel="noreferrer" className="text-[10px] text-blue-600 hover:underline flex items-center gap-1 mt-1"><i className="fas fa-video"></i> Link da Reunião</a>}
                                </div>
                                <button onClick={() => handleDelete(evt.id, evt.source)} className={`text-gray-300 p-2 opacity-0 group-hover:opacity-100 transition-opacity ${evt.source === 'GOOGLE' ? 'cursor-not-allowed hover:text-gray-400' : 'hover:text-red-500'}`}>
                                    <i className={`fas ${evt.source === 'GOOGLE' ? 'fa-lock' : 'fa-trash'}`}></i>
                                </button>
                            </div>
                            
                            {evt.source !== 'GOOGLE' && (
                                <div className="mt-3 pt-3 border-t border-gray-100 flex justify-end">
                                    <button 
                                        onClick={() => handleSendToTimesheet(evt)} 
                                        disabled={evt.timesheetSent} 
                                        className={`text-[10px] font-bold flex items-center gap-1 transition-colors px-2 py-1 rounded ${evt.timesheetSent ? 'text-green-600 bg-green-50' : 'text-gray-500 hover:text-brand-blue hover:bg-gray-50'}`}
                                    >
                                        {evt.timesheetSent ? <><i className="fas fa-check"></i> ENVIADO</> : <><i className="fas fa-stopwatch"></i> LANÇAR HORAS</>}
                                    </button>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>

            {/* MODAL EDIÇÃO */}
            {showModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-md border-t-4 border-brand-blue animate-fade-in-up">
                        <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50">
                            <h3 className="text-lg font-bold text-gray-800">
                                {form.type === 'DEADLINE' ? 'Cadastro de Prazo Fatal' : 'Evento de Agenda'}
                            </h3>
                            <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-red-500"><i className="fas fa-times"></i></button>
                        </div>
                        <div className="p-6 space-y-4">
                            <div>
                                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Título do Evento</label>
                                <input className="w-full p-2 border rounded text-sm focus:ring-2 focus:ring-brand-blue outline-none" value={form.title} onChange={e => setForm({...form, title: e.target.value})} autoFocus placeholder="Ex: Contestação - Proc. 1234" />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div><label className="block text-xs font-bold text-gray-500 uppercase mb-1">Data</label><input type="date" className="w-full p-2 border rounded text-sm" value={form.date} onChange={e => setForm({...form, date: e.target.value})} /></div>
                                <div><label className="block text-xs font-bold text-gray-500 uppercase mb-1">Hora</label><input type="time" className="w-full p-2 border rounded text-sm" value={form.time} onChange={e => setForm({...form, time: e.target.value})} /></div>
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Tipo</label>
                                <div className="grid grid-cols-2 gap-2">
                                    {Object.entries(EVENT_TYPES).filter(([k]) => k !== 'GOOGLE').map(([key, val]) => (
                                        <button key={key} onClick={() => setForm({...form, type: key})} className={`py-2 text-[10px] font-bold rounded border transition-all ${form.type === key ? val.color + ' ring-1 ring-offset-1' : 'bg-gray-50 text-gray-500 border-gray-200 hover:bg-gray-100'}`}>
                                            <i className={`fas ${val.icon} mr-1`}></i> {val.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            
                            <div>
                                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Link de Vídeo (Google Meet)</label>
                                <div className="flex gap-2">
                                    <input className="flex-1 p-2 border rounded text-xs bg-gray-50" value={form.meetLink} onChange={e => setForm({...form, meetLink: e.target.value})} placeholder="https://meet.google.com/..." />
                                    <button onClick={generateMeetLink} className="bg-green-600 text-white px-3 rounded text-[10px] font-bold hover:bg-green-700 shadow-sm" title="Gerar Link Automático"><i className="fas fa-video"></i> GERAR</button>
                                </div>
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Descrição / Observações</label>
                                <textarea className="w-full p-2 border rounded text-sm h-20 resize-none bg-gray-50 focus:bg-white transition-colors" value={form.description} onChange={e => setForm({...form, description: e.target.value})} placeholder="Detalhes do compromisso..." />
                            </div>
                        </div>
                        <div className="p-4 bg-gray-50 rounded-b-xl border-t border-gray-100">
                            <SaveCancelBar
                                onCancel={() => setShowModal(false)}
                                onSave={handleSave}
                                cancelLabel="Cancelar"
                                saveLabel="Salvar Evento"
                            />
                        </div>
                    </div>
                </div>
            )}

            {showSyncModal && (
                <GoogleSyncModal 
                    onClose={() => setShowSyncModal(false)} 
                    onConnect={handleGoogleConnect}
                    isConnected={isGoogleConnected}
                    lastSync={lastSyncTime}
                />
            )}
        </div>
    );
};